import { Check, Pencil, X } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ReceivedDateEdit } from '@/features/documents/hooks/useReceivedDateEdit'
import {
  formatReceivedDay,
  latestReceivedDay,
  receivedDayValue,
} from '@/features/documents/received-date'
import { MetaFieldLabel, MetaFieldValueRow } from './MetaField'

interface ReceivedDateFieldProps {
  /** When the case received the document (ISO); absent on an older server. */
  receivedAt: string | undefined
  /** Offered to a collaborator, who changes the day in place; left out, the field only reads. */
  edit?: ReceivedDateEdit
}

/**
 * The document detail's received date (ADR-0094): the day the case received the source, which
 * dates every event of it. A collaborator changes it in place; the server logs the change.
 */
export function ReceivedDateField({ receivedAt, edit }: ReceivedDateFieldProps) {
  const { t, i18n } = useTranslation()
  const [editing, setEditing] = useState(false)
  const label = t('documents.detail.received')

  if (editing && edit) {
    return (
      <ReceivedDayEditor
        label={label}
        initial={receivedDayValue(receivedAt)}
        edit={edit}
        onDone={() => setEditing(false)}
      />
    )
  }
  return (
    <div>
      <MetaFieldLabel>{label}</MetaFieldLabel>
      <MetaFieldValueRow>
        <span>{formatReceivedDay(receivedAt, i18n.language)}</span>
        {edit && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={t('documents.detail.receivedChange')}
            title={t('documents.detail.receivedChange')}
            onClick={() => setEditing(true)}
          >
            <Pencil />
          </Button>
        )}
      </MetaFieldValueRow>
    </div>
  )
}

interface ReceivedDayEditorProps {
  label: string
  /** The day the editor opens on (`YYYY-MM-DD`), '' when the document names none. */
  initial: string
  edit: ReceivedDateEdit
  /** Saved or cancelled: the field reads again. */
  onDone: () => void
}

/** The day as a date field with save and cancel; a refused save stays open and says so. */
function ReceivedDayEditor({ label, initial, edit, onDone }: ReceivedDayEditorProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(initial)
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (draft && (await edit.save(draft))) onDone()
  }
  return (
    <form onSubmit={submit}>
      <MetaFieldLabel>{label}</MetaFieldLabel>
      <MetaFieldValueRow>
        <Input
          type="date"
          required
          autoFocus
          aria-label={label}
          value={draft}
          max={latestReceivedDay()}
          onChange={event => setDraft(event.target.value)}
          className="h-8 w-auto min-w-0 px-2"
        />
        <Button
          type="submit"
          variant="ghost"
          size="icon-xs"
          disabled={!draft || edit.saving}
          aria-label={t('documents.detail.receivedSave')}
          title={t('documents.detail.receivedSave')}
        >
          <Check />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t('documents.detail.receivedCancel')}
          title={t('documents.detail.receivedCancel')}
          onClick={onDone}
        >
          <X />
        </Button>
      </MetaFieldValueRow>
      {edit.failed && (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {t('documents.detail.receivedSaveFailed')}
        </p>
      )}
    </form>
  )
}
